import React, { useState, useEffect } from 'react';
import { Container } from '@/components/layout/Container';
import { Stack } from '@/components/layout/Stack';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { NativeSelect } from '@/components/ui/NativeSelect';
import { useNavigationStore } from '@/store/navigationStore';
import { Settings as SettingsIcon, Save, Sparkles, Key, CheckCircle, Cloud, Eye, EyeOff, Loader2 } from 'lucide-react';
// Theme components disabled for production - uncomment when theme system is tested
// import { Palette } from 'lucide-react';
// import { ThemeToggle } from '@/components/theme/ThemeToggle';
// import { BrandSwitcher } from '@/components/ui/BrandSwitcher';
import { aiService } from '@/services/aiService';
import { BUILD_INFO } from '@/config/environment';
import { BackupManager } from '@/components/backup/BackupManager';
import { AccountSettings } from '@/components/settings/AccountSettings';
import { CompletionStorageManager } from '@/components/settings/CompletionStorageManager';
import { SyncStatus } from '@/components/sync/SyncStatus';
// Google Drive disabled for production
// import { simplifiedDriveService } from '@/services/googleDriveSimplified';
import { SubscriptionSettings } from '@/components/subscription/SubscriptionSettings';

export function Settings() {
  const { setPageInfo } = useNavigationStore();

  // AI Configuration state
  const [aiConfig, setAiConfig] = useState({
    provider: 'anthropic' as 'anthropic',
    model: '',
    enabled: true
  });
  const [userApiKey, setUserApiKey] = useState('');
  const [apiKeyVisible, setApiKeyVisible] = useState(false);
  const [apiKeyValid, setApiKeyValid] = useState<boolean | null>(null);
  const [availableModels, setAvailableModels] = useState<Array<{id: string; display_name: string}>>([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [aiSaveSuccess, setAiSaveSuccess] = useState(false);
  // Google Drive disabled for production
  // const [showGoogleDriveStatus, setShowGoogleDriveStatus] = useState<'connected' | 'error' | null>(null);

  // Load AI config on mount
  useEffect(() => {
    const config = aiService.getConfig();
    if (config) {
      setAiConfig({
        provider: 'anthropic', // Only Anthropic now
        model: config.model || '',
        enabled: config.enabled ?? true
      });
    }

    // Load saved API key
    const savedKey = aiService.getUserApiKey();
    if (savedKey) {
      setUserApiKey(savedKey);
      setApiKeyValid(true);
      fetchModels(); // Fetch models if key exists
    }

    // Google Drive status handling disabled for production
    // const urlParams = new URLSearchParams(window.location.search);
    // if (urlParams.get('drive_connected') === 'true') { ... }
  }, []);

  const handleApiKeyChange = async (key: string) => {
    setUserApiKey(key);

    if (!key) {
      aiService.saveUserApiKey('');
      setApiKeyValid(null);
      setAvailableModels([]);
      return;
    }

    // Validate format
    if (!key.startsWith('sk-ant-')) {
      setApiKeyValid(false);
      return;
    }

    try {
      // Test the key by fetching models
      aiService.saveUserApiKey(key);
      await fetchModels();
      setApiKeyValid(true);
    } catch (error) {
      setApiKeyValid(false);
      aiService.saveUserApiKey(''); // Clear invalid key
    }
  };

  const fetchModels = async () => {
    setLoadingModels(true);
    try {
      const models = await aiService.fetchAnthropicModels();
      setAvailableModels(models);

      // Set default to Sonnet 4 if available
      const sonnet4 = models.find(m =>
        m.id.includes('sonnet-4') ||
        m.id.includes('sonnet-20241022')
      );
      if (sonnet4 && !aiConfig.model) {
        setAiConfig(prev => ({ ...prev, model: sonnet4.id }));
      }
    } catch (error) {
      console.error('Failed to fetch models:', error);
      setApiKeyValid(false);
    } finally {
      setLoadingModels(false);
    }
  };

  // AI save handler
  const handleSaveAIConfig = () => {
    // Save AI model and enabled status
    aiService.saveConfig(aiConfig);
    setAiSaveSuccess(true);
    setTimeout(() => setAiSaveSuccess(false), 3000);
  };


  // Set page info on mount
  React.useEffect(() => {
    setPageInfo('Settings');
  }, [setPageInfo]);

  return (
    <Container size="lg" className="py-6">
      <Stack spacing="lg">
        <div className="flex flex-col gap-6">

          {/* AI Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Sparkles className="h-5 w-5 mr-2" />
                AI Configuration
              </CardTitle>
              <CardDescription>
                Configure AI assistance for creating aspirations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Stack spacing="md">

                {/* User API Key Input */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Anthropic API Key (Required)
                  </label>
                  <div className="relative">
                    <input
                      type={apiKeyVisible ? 'text' : 'password'}
                      className={`w-full p-2 pr-20 border rounded-md bg-background ${
                        apiKeyValid === false ? 'border-red-500' :
                        apiKeyValid === true ? 'border-green-500' : ''
                      }`}
                      placeholder="sk-ant-..."
                      value={userApiKey}
                      onChange={(e) => handleApiKeyChange(e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setApiKeyVisible(!apiKeyVisible)}
                      className="absolute right-10 top-2.5 text-gray-500 hover:text-gray-700"
                    >
                      {apiKeyVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                    {apiKeyValid === true && (
                      <CheckCircle className="absolute right-2 top-3 h-4 w-4 text-green-500" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Your API key is stored locally and included in data backups.
                    Required for all AI features.
                  </p>
                  {apiKeyValid === false && (
                    <p className="text-xs text-red-500 mt-1">
                      Invalid API key. Please check your key and try again.
                    </p>
                  )}
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                    <p className="font-medium text-blue-700">Get your API key:</p>
                    <p className="text-blue-600">
                      Visit <a href="https://console.anthropic.com/" target="_blank" rel="noopener noreferrer" className="underline">console.anthropic.com</a> to create an API key
                    </p>
                  </div>
                </div>

                {/* Model Dropdown - Only show if API key is valid */}
                {apiKeyValid && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">AI Model</label>
                    {loadingModels ? (
                      <div className="flex items-center justify-center p-4">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        <span className="text-sm text-muted-foreground">Loading models...</span>
                      </div>
                    ) : (
                      <NativeSelect
                        value={aiConfig.model}
                        onChange={(e) => setAiConfig({ ...aiConfig, model: e.target.value })}
                        disabled={availableModels.length === 0}
                      >
                        {availableModels.length === 0 ? (
                          <option value="">No models available</option>
                        ) : (
                          availableModels.map(model => (
                            <option key={model.id} value={model.id}>
                              {model.display_name || model.id}
                            </option>
                          ))
                        )}
                      </NativeSelect>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      Claude 3.5 Sonnet is recommended for best results
                    </p>
                  </div>
                )}

                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Enable AI Features</p>
                    <p className="text-xs text-muted-foreground">Show AI assistant in forms</p>
                  </div>
                  <input
                    type="checkbox"
                    className="rounded"
                    checked={aiConfig.enabled}
                    onChange={(e) => setAiConfig({ ...aiConfig, enabled: e.target.checked })}
                  />
                </div>

                {/* Security Notice */}
                <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                  <div className="flex items-start gap-2">
                    <Key className="h-4 w-4 text-green-600 mt-0.5" />
                    <div className="text-xs text-green-700">
                      <p className="font-medium">Privacy & Security</p>
                      <p>Your API key is stored locally in your browser and never sent to our servers.</p>
                      <p className="mt-1">Only you have access to your API key and usage costs.</p>
                    </div>
                  </div>
                </div>
                
                {aiSaveSuccess && (
                  <Alert className="bg-green-50 text-green-800 border-green-200 text-sm">
                    AI preferences saved successfully!
                  </Alert>
                )}
                
                <div className="pt-4">
                  <Button
                    onClick={handleSaveAIConfig}
                    disabled={!apiKeyValid}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save AI Preferences
                  </Button>
                </div>
              </Stack>
            </CardContent>
          </Card>

          {/* Subscription */}
          <SubscriptionSettings />

          {/* Appearance - TEMPORARILY HIDDEN until theme system is fully tested
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Palette className="h-5 w-5 mr-2" />
                Appearance
              </CardTitle>
              <CardDescription>
                Customize the look and feel of your interface
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Stack spacing="md">
                <ThemeToggle />
                <BrandSwitcher />
              </Stack>
            </CardContent>
          </Card>
          */}

          {/* Sync Status */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Cloud className="h-5 w-5 mr-2" />
                Sync Status
              </CardTitle>
              <CardDescription>
                Monitor your data synchronization status
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SyncStatus showDetails />
            </CardContent>
          </Card>

          {/* Backup & Storage */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Cloud className="h-5 w-5 mr-2" />
                Backup & Storage
              </CardTitle>
              <CardDescription>
                Keep your data safe with automatic backups to your personal cloud storage
              </CardDescription>
            </CardHeader>
            <CardContent>
              <BackupManager />
            </CardContent>
          </Card>

          {/* Completion Data Storage */}
          <CompletionStorageManager />
          
          {/* General */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <SettingsIcon className="h-5 w-5 mr-2" />
                General
              </CardTitle>
              <CardDescription>
                General application preferences
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Stack spacing="md">
                <div>
                  <p className="text-sm font-medium mb-2">Timezone</p>
                  <NativeSelect>
                    <option>UTC-8 (Pacific Time)</option>
                    <option>UTC-5 (Eastern Time)</option>
                    <option>UTC+0 (Greenwich Mean Time)</option>
                  </NativeSelect>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Show completed items</p>
                    <p className="text-xs text-muted-foreground">Display completed tasks in lists</p>
                  </div>
                  <input type="checkbox" className="rounded" defaultChecked />
                </div>
                <div className="pt-4">
                  <Button>
                    <Save className="h-4 w-4 mr-2" />
                    Save Settings
                  </Button>
                </div>
              </Stack>
            </CardContent>
          </Card>

          {/* Account & Security */}
          <AccountSettings />


          {/* Version Info */}
          <div className="text-center text-xs text-muted-foreground pt-4 pb-8 space-y-1">
            <p>GodMode v{BUILD_INFO.version}</p>
            <p className="font-mono">Build: {BUILD_INFO.commitHash} • {new Date(BUILD_INFO.buildTime).toLocaleString()}</p>
          </div>
        </div>
      </Stack>
    </Container>
  );
}